'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { RefreshCw, ArrowRight, Sparkles, User } from 'lucide-react'

interface WelcomeBackModalProps {
  show: boolean
  name: string
  lastStep: number
  onContinue: () => void
  onStartOver: () => void
}

// Nomes dos steps para exibição
const STEP_NAMES: Record<number, string> = {
  1: 'seu nome',
  2: 'seus contatos',
  3: 'perfil',
  4: 'situação atual',
  5: 'objetivo',
  6: 'funcionalidades',
  7: 'marca',
  8: 'conteúdo',
  9: 'prazo',
  10: 'detalhes',
  11: 'pacote',
  12: 'finalização'
}

function getFirstName(fullName: string): string {
  return fullName.split(' ')[0] || fullName
}

export function WelcomeBackModal({
  show,
  name,
  lastStep,
  onContinue,
  onStartOver
}: WelcomeBackModalProps) {
  const firstName = getFirstName(name)
  const stepName = STEP_NAMES[lastStep] || `etapa ${lastStep}`
  const progress = Math.round((lastStep / 12) * 100)

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="w-full max-w-md bg-white dark:bg-black border border-purple-500/20 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header com gradiente */}
            <div className="bg-gradient-to-br from-violet-600 to-fuchsia-600 p-6 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="w-16 h-16 mx-auto mb-4 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
              >
                <User size={32} className="text-white" />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-bold text-white mb-1"
              >
                Bem-vindo de volta, {firstName}!
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-white/80 text-sm"
              >
                Que bom ver você novamente
              </motion.p>
            </div>

            {/* Conteúdo */}
            <div className="p-6">
              {/* Progress salvo */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mb-6 p-4 bg-indigo-950/5 dark:bg-white/5 rounded-xl border border-purple-500/20 dark:border-white/10"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Sparkles size={20} className="text-violet-500 dark:text-violet-400" />
                  </div>
                  <div>
                    <p className="text-indigo-950 dark:text-white font-medium">Seu progresso foi salvo</p>
                    <p className="text-indigo-950/50 dark:text-white/50 text-sm">Você estava em: {stepName}</p>
                  </div>
                </div>

                {/* Barra de progresso */}
                <div className="h-2 bg-indigo-950/10 dark:bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ delay: 0.7, duration: 0.5 }}
                    className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full"
                  />
                </div>
                <p className="text-right text-xs text-indigo-950/40 dark:text-white/40 mt-1">{progress}% completo</p>
              </motion.div>

              {/* Botões */}
              <div className="space-y-3">
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  onClick={onContinue}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded-xl transition-all active:scale-[0.98] shadow-lg shadow-purple-500/25"
                >
                  <span>Continuar de onde parei</span>
                  <ArrowRight size={18} />
                </motion.button>

                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  onClick={onStartOver}
                  className="w-full flex items-center justify-center gap-2 py-3 text-indigo-950/60 dark:text-white/60 hover:text-indigo-950/80 dark:hover:text-white/80 font-medium transition-colors"
                >
                  <RefreshCw size={16} />
                  <span>Começar do zero</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
